import React, { useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./ResetPassword.scss";
import lockImg from "../assets/img/lock-illustration.png";
import BtnLoad from "../components/BtnLoad";
import { UnoStoreHooks } from "@simulanisindia/ui";
import { useEffect } from "react";
import apiClient from "../apiClient";
import { SET_NEW_PASSWORD } from "../apiClient/apiEndpoints";
const { useUnoSelector } = UnoStoreHooks;

const ResetPassword = () => {
  const configData = useUnoSelector((state) => state.config);
  const passwordPolicy = configData?.config?.passwordPolicy;
  const API_URL = configData?.config?.UNO_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const link = query.get("link");

  const passwordRequirements = useMemo(() => {
    if (!passwordPolicy) return [];
    const reqs = [];
    if (passwordPolicy.upperCase) {
      reqs.push({
        id: "upper",
        label: "At least 1 uppercase letter",
        test: (v) => /[A-Z]/.test(v),
      });
    }
    if (passwordPolicy.number) {
      reqs.push({
        id: "number",
        label: "At least 1 number",
        test: (v) => /[0-9]/.test(v),
      });
    }
    if (passwordPolicy.specialChar) {
      reqs.push({
        id: "special",
        label: "At least 1 special character",
        test: (v) => /[^A-Za-z0-9]/.test(v),
      });
    }
    if (passwordPolicy.min) {
      reqs.push({
        id: "length",
        label: `At least ${passwordPolicy.min} characters`,
        test: (v) => (v || "").length >= passwordPolicy.min,
      });
    }
    if (passwordPolicy.max) {
      reqs.push({
        id: "maxLength",
        label: `No more than ${passwordPolicy.max} characters`,
        test: (v) => (v || "").length <= passwordPolicy.max,
      });
    }

    if (true) {
      reqs.push({
        id: "passwordMatch",
        label: `Password matched`,
        test: (v, confirmPassword) => confirmPassword && confirmPassword == v,
      });
    }
    return reqs;
  }, [passwordPolicy]);

  const results = passwordRequirements.map((r) => ({ ...r, ok: r.test(password, confirmPassword) }));
  const isValid = results.every((r) => r.ok);
  const passwordsMatch = password && password === confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!isValid) return setMessage({ type: "error", text: "Please meet all password requirements." });
    if (!passwordsMatch) return setMessage({ type: "error", text: "Passwords do not match." });

    setSubmitting(true);
    try {
      await apiClient.post(`${API_URL}${SET_NEW_PASSWORD}`, {
        token: link,
        password: password,
      });
      setMessage({ type: "success", text: "Password reset successful. You can now log in." });
      setTimeout(() => navigate("/gateway"), 2200);
    } catch (err) {
      console.log(err.message);
      setMessage({ type: "error", text: err?.response?.data?.message });
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (!link && API_URL) {
      navigate("/gateway");
    }
  }, [link, API_URL]);

  return (
    <div className="reset-password-page white-bg">
      <div className="bg-ball ball-a" />
      <div className="bg-ball ball-b" />
      <div className="bg-ball ball-c" />

      <div className="row h-100 main-reset-parent">
        <div className="col-lg-6 d-flex">
          <div className="reset-left">
            <div className="hero-copy">
              <h1>Secure your account</h1>
              <p>Choose a strong password to keep your account safe. Please follow the password policy below.</p>
            </div>
            <div className="hero-illustration">
              <img src={lockImg} alt="security" />
            </div>
          </div>
        </div>
        <div className="col-lg-6  d-flex">
          <div className="reset-right">
            <div className="card moved-left">
              <h2>Reset Password</h2>
              <form onSubmit={handleSubmit} className="form">
                <label className="label">Enter Password</label>
                <div className="field">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="eye svg-eye"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label="toggle password"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 5c4.97 0 9 3.58 10 7-1 3.42-5.03 7-10 7S3 15.42 2 12c1-3.42 5.03-7 10-7z"
                          stroke="#2f3a79"
                          strokeWidth="1.2"
                        />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#2f3a79" strokeWidth="1.2" />
                        <path d="M12 9v6" stroke="#2f3a79" strokeWidth="1.2" />
                      </svg>
                    )}
                  </button>
                </div>

                <label className="label">Confirm Password</label>
                <div className="field">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    className="eye svg-eye"
                    onClick={() => setShowConfirm((s) => !s)}
                    aria-label="toggle confirm"
                  >
                    {showConfirm ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 5c4.97 0 9 3.58 10 7-1 3.42-5.03 7-10 7S3 15.42 2 12c1-3.42 5.03-7 10-7z"
                          stroke="#2f3a79"
                          strokeWidth="1.2"
                        />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#2f3a79" strokeWidth="1.2" />
                        <path d="M12 9v6" stroke="#2f3a79" strokeWidth="1.2" />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="requirements modern">
                  <ul>
                    {results.map((r) => (
                      <li key={r.id} className={r.ok ? "met" : "not-met"}>
                        <span className="status">{r.ok ? "✔" : ""}</span>
                        <span className="text">{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {message.text && <div className={"msg " + message.type}>{message.text}</div>}

                <button className="submit app-blue" type="submit" disabled={!isValid || !passwordsMatch || submitting}>
                  {submitting ? <BtnLoad /> : "Reset Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
